import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import {
  ComponentProps,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import * as SC from "./masked-scroll-area.styles";

export const MaskedScrollArea = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof ScrollAreaPrimitive.Root>
>(({ children, ...props }, ref) => {
  const fullContentRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  const [isContentScrollable, setIsContentScrollable] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const updateDimensions = useCallback(() => {
    const viewport = viewportRef.current;
    const fullContent = fullContentRef.current;
    if (!viewport || !fullContent) return;

    const isScrollable = fullContent.offsetHeight > viewport.clientHeight;
    setIsContentScrollable(isScrollable);
  }, []);

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    const fullContent = fullContentRef.current;
    if (!fullContent) return;

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(fullContent);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, [updateDimensions]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === topSentinelRef.current) {
            setShowTopFade(!entry.isIntersecting);
          }
          if (entry.target === bottomSentinelRef.current) {
            setShowBottomFade(!entry.isIntersecting);
          }
        }
      },
      { root: viewport, threshold: 0 }
    );

    if (topSentinelRef.current) observer.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) observer.observe(bottomSentinelRef.current);

    return () => observer.disconnect();
  }, [children]);

  return (
    <SC.Root ref={ref} {...props}>
      <SC.Viewport
        ref={viewportRef}
        isContentScrollable={isContentScrollable}
        showTopFade={showTopFade}
        showBottomFade={showBottomFade}
      >
        <SC.FullContent ref={fullContentRef}>
          <SC.TopScrollSentinel ref={topSentinelRef} />
          {children}
          <SC.BottomScrollSentinel ref={bottomSentinelRef} />
        </SC.FullContent>
      </SC.Viewport>

      <SC.Scrollbar orientation="vertical">
        <SC.Thumb />
      </SC.Scrollbar>

      <SC.Corner />
    </SC.Root>
  );
});

MaskedScrollArea.displayName = "MaskedScrollArea";
